import { CameraIcon } from '@heroicons/react/24/outline';
import { requestComfortScan } from '../../../../../services/dmMonitoringControl';
import { useToast } from '../../../../../providers/ToastProvider';
import { ConfirmLayout } from './ConfirmLayout';
import { DmButton, dmTealInputClass } from './DmControlPrimitives';
import { COMFORT_COMMENT_MAX_LENGTH, comfortScanParams } from './patientRequests';
import { useDmControlMutation } from './useDmControlMutation';

interface RequestScanStepProps {
    patientId: string;
    patientName?: string;
    message: string;
    onMessageChange: (message: string) => void;
    onCancel: () => void;
    onDone: () => void;
}

// Confirm step for an extra (comfort) scan, outside the regular schedule.
export function RequestScanStep({
    patientId,
    patientName,
    message,
    onMessageChange,
    onCancel,
    onDone
}: RequestScanStepProps) {
    const { showToast } = useToast();
    const action = useDmControlMutation(
        patientId,
        () => requestComfortScan(patientId, comfortScanParams(message)),
        () => {
            showToast('Additional scan requested', 'success');
            onDone();
        }
    );
    const busy = action.saving;
    return (
        <ConfirmLayout
            fill
            error={action.error}
            cancel={
                <DmButton onClick={onCancel} disabled={busy}>
                    Cancel
                </DmButton>
            }
            confirm={
                <DmButton
                    kind="start"
                    icon={CameraIcon}
                    disabled={busy}
                    loading={busy}
                    onClick={() => action.run()}
                >
                    {busy ? 'Requesting…' : 'Request scan'}
                </DmButton>
            }
        >
            <p className="text-sm text-gray-700">
                Ask {patientName ?? 'the patient'} for an extra scan now, on top of their regular
                schedule.
            </p>
            {/* Takes the rest of the area, so the message box grows wherever there is room. */}
            <div className="flex min-h-0 flex-1 flex-col">
                <div className="mb-0.5 flex items-baseline justify-between gap-2">
                    <label
                        htmlFor="dm-comfort-message"
                        className="text-xs font-medium text-gray-500"
                    >
                        Message to the patient
                        <span className="font-normal text-gray-400"> · optional</span>
                    </label>
                    <span className="text-[11px] text-gray-400 tabular-nums">
                        {message.length}/{COMFORT_COMMENT_MAX_LENGTH}
                    </span>
                </div>
                <textarea
                    id="dm-comfort-message"
                    value={message}
                    onChange={(e) => onMessageChange(e.target.value)}
                    maxLength={COMFORT_COMMENT_MAX_LENGTH}
                    rows={2}
                    className={`${dmTealInputClass} w-full flex-1 resize-none`}
                />
            </div>
        </ConfirmLayout>
    );
}
